import {
	Button,
	type ColumnDef,
	DataTable,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	FilterBuilder,
	type FilterField,
	Input,
	Label,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { Ban, MailPlus, MoreVertical, RefreshCw } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DrawerForm from "src/components/drawer-form/drawer-form";
import { StatusBadge } from "src/components/status-badge";
import userProvider, {
	type Invitation,
	type PaginatedInvitationsResponse,
} from "src/providers/user-provider";
import { getStatusConfig, STATUS_CONFIG } from "src/utils/status-config";

// Generate status options from unified config
const STATUS_OPTIONS = [
	{
		value: "pending",
		label: STATUS_CONFIG.pending.label,
		icon: (
			<TintedBackgroundIcon
				icon={STATUS_CONFIG.pending.icon}
				color={STATUS_CONFIG.pending.color}
				size="sm"
			/>
		),
	},
	{
		value: "accepted",
		label: STATUS_CONFIG.accepted.label,
		icon: (
			<TintedBackgroundIcon
				icon={STATUS_CONFIG.accepted.icon}
				color={STATUS_CONFIG.accepted.color}
				size="sm"
			/>
		),
	},
	{
		value: "revoked",
		label: STATUS_CONFIG.revoked.label,
		icon: (
			<TintedBackgroundIcon
				icon={STATUS_CONFIG.revoked.icon}
				color={STATUS_CONFIG.revoked.color}
				size="sm"
			/>
		),
	},
];

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: STATUS_OPTIONS,
	},
];

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const getStatusBadgeType = (
	status: string,
): "success" | "warning" | "error" | "info" | "default" => {
	const config = getStatusConfig(status);
	return config.badgeType === "danger" ? "error" : config.badgeType;
};

const ManageInvitationsPage: React.FC = () => {
	const [invitations, setInvitations] = React.useState<Invitation[]>([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isCreating, setIsCreating] = React.useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
	const [newEmail, setNewEmail] = React.useState("");

	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: { status?: string } = {};

		for (const f of filters) {
			if (f.operator === "is" && f.field === "status") {
				result.status = f.value;
			}
		}

		return result;
	}, [filters]);

	const loadInvitations = React.useCallback(async () => {
		try {
			setIsLoading(true);

			const response: PaginatedInvitationsResponse =
				await userProvider.listInvitations({
					limit,
					offset,
					status: apiFilters.status,
				});

			setInvitations(response.invitations);
			setTotalCount(response.totalCount);
		} catch (error) {
			console.error("Failed to load invitations:", error);
			toast.error("Failed to load invitations", {
				description: "Please try again later",
			});
		} finally {
			setIsLoading(false);
		}
	}, [limit, offset, apiFilters.status]);

	React.useEffect(() => {
		loadInvitations();
	}, [loadInvitations]);

	const handleCreateInvitation = React.useCallback(async () => {
		if (!newEmail.trim()) {
			toast.error("Email is required");
			return;
		}

		try {
			setIsCreating(true);
			await userProvider.createInvitation(newEmail.trim());
			toast.success("Invitation sent successfully");
			setIsDrawerOpen(false);
			setNewEmail("");
			loadInvitations();
		} catch (error: unknown) {
			console.error("Failed to create invitation:", error);
			const err = error as { response?: { data?: { status?: string } } };
			if (err.response?.data?.status === "user_exists") {
				toast.error("User already exists with this email");
			} else if (err.response?.data?.status === "invitation_exists") {
				toast.error("Pending invitation already exists for this email");
			} else {
				toast.error("Failed to send invitation");
			}
		} finally {
			setIsCreating(false);
		}
	}, [newEmail, loadInvitations]);

	const handleRevokeInvitation = React.useCallback(
		async (invitationId: number, email: string) => {
			try {
				await userProvider.revokeInvitation(invitationId);
				toast.success(`Invitation for ${email} revoked`);
				loadInvitations();
			} catch (error) {
				console.error("Failed to revoke invitation:", error);
				toast.error("Failed to revoke invitation");
			}
		},
		[loadInvitations],
	);

	const handleResendInvitation = React.useCallback(
		async (invitationId: number, email: string) => {
			try {
				await userProvider.resendInvitation(invitationId);
				toast.success(`Invitation resent to ${email}`);
			} catch (error) {
				console.error("Failed to resend invitation:", error);
				toast.error("Failed to resend invitation");
			}
		},
		[],
	);

	const columns: ColumnDef<Invitation>[] = React.useMemo(
		() => [
			{
				id: "email",
				header: "Email",
				cell: (invitation) => (
					<span className="font-medium">{invitation.email}</span>
				),
			},
			{
				id: "status",
				header: "Status",
				cell: (invitation) => (
					<StatusBadge type={getStatusBadgeType(invitation.status)}>
						{invitation.status.charAt(0).toUpperCase() +
							invitation.status.slice(1)}
					</StatusBadge>
				),
			},
			{
				id: "createdAt",
				header: "Created At",
				cell: (invitation) => formatDate(invitation.createdAt),
			},
			{
				id: "expiresAt",
				header: "Expires At",
				cell: (invitation) => formatDate(invitation.expiresAt),
			},
			{
				id: "actions",
				header: "",
				className: "w-[50px]",
				cell: (invitation) =>
					invitation.status === "pending" ? (
						<div
							className="flex justify-end"
							onClick={(e) => e.stopPropagation()}
						>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="icon">
										<MoreVertical className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										className="cursor-pointer"
										onClick={() =>
											handleResendInvitation(invitation.id, invitation.email)
										}
									>
										<RefreshCw className="mr-2 h-4 w-4" />
										Resend
									</DropdownMenuItem>
									<DropdownMenuItem
										className="text-destructive focus:text-destructive cursor-pointer"
										onClick={() =>
											handleRevokeInvitation(invitation.id, invitation.email)
										}
									>
										<Ban className="mr-2 h-4 w-4" />
										Revoke
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					) : null,
			},
		],
		[handleResendInvitation, handleRevokeInvitation],
	);

	const hasActiveFilters = filters.length > 0;

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Invitation Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total invitation{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
				<Button onClick={() => setIsDrawerOpen(true)}>
					<MailPlus className="h-4 w-4 mr-2" />
					Invite User
				</Button>
			</div>

			<div className="flex gap-4 flex-wrap items-center">
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={invitations}
				isLoading={isLoading}
				getRowId={(row) => String(row.id)}
				emptyMessage={
					hasActiveFilters ? "No invitations found" : "No invitations yet"
				}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your filter criteria"
						: "Invite users to join the platform"
				}
				emptyIcon={<MailPlus className="h-8 w-8" />}
				emptyAction={
					!hasActiveFilters ? (
						<Button onClick={() => setIsDrawerOpen(true)}>
							<MailPlus className="h-4 w-4 mr-2" />
							Send First Invitation
						</Button>
					) : undefined
				}
				pagination={
					totalCount > 0
						? {
								page,
								pageSize: limit,
								totalCount,
								onPageChange: setPage,
								onPageSizeChange: setLimit,
							}
						: undefined
				}
			/>

			<DrawerForm
				title="Invite New User"
				description="Send an invitation email to a new user"
				isOpen={isDrawerOpen}
				onClose={() => {
					setIsDrawerOpen(false);
					setNewEmail("");
				}}
				onSubmit={handleCreateInvitation}
				isSubmitting={isCreating}
				submitText="Send Invitation"
				width={512}
			>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="email">Email Address</Label>
						<Input
							id="email"
							type="email"
							placeholder="user@example.com"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !isCreating) {
									handleCreateInvitation();
								}
							}}
						/>
					</div>
				</div>
			</DrawerForm>
		</div>
	);
};

export default ManageInvitationsPage;
